import React from 'react';
import useMenuState from 'components/Layout/MenuStateProvider/useMenuState';
import { DRAWER_WIDTH, DRAWER_WIDTH_CLOSED } from 'components/Layout/utils';
import { CONTENT_WITH_SUB_NAV_OFFSET } from 'components/Layout/Layout';
import DraggableLegend from '../DraggableLegend';

const legendDefaultY = () => {
  const toolbarHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--stopes-toolbar-height')) || 0;
  return CONTENT_WITH_SUB_NAV_OFFSET + toolbarHeight + 12;
};

interface LegendItem {
  color: string;
  label: string;
  value?: string | number;
}

interface SceneLegendContextType {
  showLegend: boolean;
  setShowLegend: (show: boolean) => void;
  legendData: {
    title: string;
    items: LegendItem[];
  } | null;
  setLegendData: (data: { title: string; items: LegendItem[] } | null) => void;
}

const SceneLegendContext = React.createContext<SceneLegendContextType | undefined>(undefined);

export const useSceneLegend = () => {
  const context = React.useContext(SceneLegendContext);
  if (!context) {
    throw new Error('useSceneLegend must be used within a SceneLegendProvider');
  }
  return context;
};

interface SceneLegendProviderProps {
  children: React.ReactNode;
}

const legendItemsEqual = (a: LegendItem[], b: LegendItem[]) => (
  a.length === b.length
  && a.every((item, index) => (
    item.color === b[index].color
    && item.label === b[index].label
    && item.value === b[index].value
  ))
);

const legendDataEqual = (
  a: { title: string; items: LegendItem[] } | null,
  b: { title: string; items: LegendItem[] } | null,
) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.title === b.title && legendItemsEqual(a.items, b.items);
};

export const SceneLegendProvider = ({ children }: SceneLegendProviderProps) => {
  const menuOpen = useMenuState();
  const sidebarWidth = menuOpen ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED;

  const [showLegend, setShowLegendInternal] = React.useState(false);
  const showLegendRef = React.useRef(false);
  const [legendData, setLegendDataInternal] = React.useState<{ title: string; items: LegendItem[] } | null>(null);
  const [legendPosition, setLegendPosition] = React.useState({ x: sidebarWidth + 120, y: legendDefaultY() });

  const setShowLegend = React.useCallback((show: boolean) => {
    if (show && !showLegendRef.current) {
      const currentSidebarWidth = menuOpen ? DRAWER_WIDTH : DRAWER_WIDTH_CLOSED;
      setLegendPosition({ x: currentSidebarWidth + 120, y: legendDefaultY() });
    }
    showLegendRef.current = show;
    setShowLegendInternal((current) => (current === show ? current : show));
  }, [menuOpen]);

  const setLegendData = React.useCallback((data: { title: string; items: LegendItem[] } | null) => {
    setLegendDataInternal((current) => (legendDataEqual(current, data) ? current : data));
  }, []);

  const contextValue: SceneLegendContextType = React.useMemo(() => ({
    showLegend,
    setShowLegend,
    legendData,
    setLegendData,
  }), [legendData, setLegendData, setShowLegend, showLegend]);

  return (
    <SceneLegendContext.Provider value={contextValue}>
      {children}
      {showLegend && legendData && (
        <DraggableLegend
          title={legendData.title}
          items={legendData.items}
          isVisible
          position={legendPosition}
          onPositionChange={setLegendPosition}
          onClose={() => {
            setShowLegend(false);
            setLegendData(null);
          }}
        />
      )}
    </SceneLegendContext.Provider>
  );
};

export default SceneLegendProvider;
